import { LogoMark } from '#src/base/logo/logo_mark.tsx';
import { cn } from '#src/styling/cn.ts';
import type { ComponentPropsWithoutRef } from 'react';

export type CardProps = ComponentPropsWithoutRef<'div'> & BannerProps;

/** A card with optional edge-to-edge banner. Override outer styles with `className`. */
export const Card = ({ className, bannerImage, bannerImageAlt, children, ...props }: CardProps) => {
  if (bannerImage) {
    return (
      <div
        className={cn('w-full overflow-hidden rounded-3xl bg-white shadow-sm', className)}
        {...props}
      >
        <div className="h-32 w-full sm:h-40">
          <img
            src={bannerImage}
            alt={bannerImageAlt}
            className="h-full w-full object-cover object-center"
          />
        </div>
        <div className="flex min-h-0 flex-1 flex-col px-6 py-8 md:px-8">{children}</div>
      </div>
    );
  }

  return (
    <div
      className={cn('w-full rounded-3xl bg-white px-6 py-8 shadow-sm md:px-8', className)}
      {...props}
    >
      {children}
    </div>
  );
};

/**
 * Standardized logo mark that links back to the home page.
 * Use inside a {@link Card} to keep the logo size and placement consistent across all pages.
 */
export const CardLogo = ({ className, ...props }: ComponentPropsWithoutRef<'a'>) => {
  return (
    <a href="/" className={cn('inline-block w-auto', className)} {...props}>
      <LogoMark size="2xs" />
    </a>
  );
};

type BannerProps =
  | { bannerImage: string; bannerImageAlt: string }
  | { bannerImage?: never; bannerImageAlt?: never };
